import { AnyAction, createAsyncThunk, createEntityAdapter, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { groupBy } from 'lodash';
import { DateTime } from 'luxon';
import { itemsClient } from '../../../api/client';
import {
  ItemRequestNewItem,
  ItemRequestUpdateItem,
  QueryActivityDecode,
  QueryDependencies,
  QueryItemCategory,
  QueryItemDetails,
  QueryItemEquipmentBreakdownRow,
  QueryItemUtilizationPoint,
} from '../../../api/schema';
import { getPeriodMapped, getSeen } from '../../../helpers/dataHelper';
import { getPagination, keysToId } from '../../../helpers/sliceHelper';
import { RootState } from '../../store';
import { getUserAbilities } from '../users/usersSlice';
import {
  ItemRequestNewItemMappedType,
  ItemRequestNewItemType,
  ItemsLeftBehindAgg,
  ItemsLeftBehindAggType,
  ItemsRecoveredAgg,
  ItemsRecoveredAggType,
  ItemsRecoveryProgress,
  ItemsRecoveryProgressType,
  JobPointType,
  LastFormDataType,
  ListItemActivityListType,
  ListItemActivityQuery,
  ListItemActivityType,
  ListItemsLeftBehindAggQuery,
  ListItemsRecoveryProgressQuery,
  ProductItemsQuery,
  ProductToolsState,
  ProductToolsType,
  QueryToolDetailsType,
  ToolsType,
  UtilizationPointType,
} from './toolsInterface';

export const listProductToolsAdapter = createEntityAdapter<ProductToolsType>({
  selectId: (product) => product.id,
});
export const toolDetailsAdapter = createEntityAdapter<QueryToolDetailsType>({
  selectId: (item) => item.id || '',
});
export const toolDependenciesAdapter = createEntityAdapter<QueryDependencies>({
  selectId: (dependency) => dependency.id || '',
});
export const listItemsLeftBehindAggAdapter = createEntityAdapter<ItemsLeftBehindAggType>({
  selectId: (product) => product.id,
});

export const listItemsRecoveredAggAdapter = createEntityAdapter<ItemsRecoveredAggType>({
  selectId: (product) => product.id,
});

export const listItemsRecoveryProgressAdapter = createEntityAdapter<ItemsRecoveryProgressType>({
  selectId: (entry) => entry.id,
});

export const listItemActivityAdapter = createEntityAdapter<ListItemActivityType>({
  selectId: (list) => list.id || '',
});

export const createToolAdapter = createEntityAdapter<ItemRequestNewItemType>({
  selectId: (item) => item.id || '',
});

export const listProductTools = createAsyncThunk('tools/listProductTools', async (query: ProductItemsQuery) => {
  const argumentsKeys = [
    'ownerId',
    'ownerWarehouseId',
    'trackingWarehouseId',
    'scanningOwnerId',
    'productId',
    'manufacturerId',
    'productCategory',
    'productTypeId',
    'ownerTypeId',
    'locationVisitId',
    'trackingSiteId',
    'jobSiteId',
    'scanStatus',
    'active',
    'createdBegin',
    'createdEnd',
    'search',
    'sortBy',
    'offset',
    'limit',
    'options',
  ];
  return itemsClient.listItems.apply(
    this,
    argumentsKeys.map((key) => query[key]) as [
      ownerId?: string,
      ownerWarehouseId?: string,
      trackingWarehouseId?: string,
      scanningOwnerId?: string,
      productId?: string,
      manufacturerId?: string,
      productCategory?: string,
      productTypeId?: string,
      ownerTypeId?: number,
      locationVisitId?: string,
      trackingSiteId?: string,
      jobSiteId?: string,
      scanStatus?: string,
      active?: string,
      createdBegin?: string,
      createdEnd?: string,
      search?: string,
      sortBy?: string,
      offset?: number,
      limit?: number,
      options?: Record<string, unknown>,
    ],
  );
});

export const getToolById = createAsyncThunk('tools/getToolById', async (itemId: string) => itemsClient.getItemById(itemId));
export const updateTool = createAsyncThunk('tools/updateTool', async (values: ItemRequestUpdateItem) => itemsClient.updateItem(values));
export const toolCategories = createAsyncThunk('tools/toolCategories', async () => itemsClient.itemCategories());
export const itemEquipmentSummary = createAsyncThunk('tools/itemEquipmentSummary', async () => itemsClient.itemEquipmentSummary());
export const itemUtilizationHistory = createAsyncThunk('tools/itemUtilizationHistory', async () => itemsClient.itemUtilizationHistory());
export const itemEquipmentBreakdown = createAsyncThunk('tools/itemEquipmentBreakdown', async () => itemsClient.itemEquipmentBreakdown());
export const itemJobHistory = createAsyncThunk('tools/itemJobHistory', async (productId: string | undefined) => itemsClient.itemJobHistory(productId));
// Scoped to a product or to a single item; the key keeps the two apart in the
// store so a product page and an item page cannot overwrite each other.
export const itemDeploymentStats = createAsyncThunk('tools/itemDeploymentStats', async (scope: { productId?: string; itemId?: string }) =>
  itemsClient.itemDeploymentStats(scope.productId, scope.itemId),
);
export const itemTimeline = createAsyncThunk('tools/itemTimeline', async (itemId: string) => itemsClient.itemTimeline(itemId));
export const itemProductUtilization = createAsyncThunk('tools/itemProductUtilization', async (query: { manufacturerId: string; productId?: string }) =>
  itemsClient.itemProductUtilization(query.manufacturerId, query.productId),
);
export const itemProductUtilizationAll = createAsyncThunk('tools/itemProductUtilizationAll', async () => itemsClient.itemProductUtilization());
export const getToolDependencies = createAsyncThunk('tools/getToolDependencies', async (itemId: string) => itemsClient.getItemDependencies(itemId));
// TODO add reducers
export const createTool = createAsyncThunk('tools/createTool', async (values: ItemRequestNewItem) => itemsClient.createItem(values));
export const getItemNextSerialNumber = createAsyncThunk('tools/getItemNextSerialNumber', async () => itemsClient.getItemNextSerialNumber());
export const listActivityDecodes = createAsyncThunk('tools/listActivityDecodes', async () => itemsClient.listActivityDecodes());

export const listItemActivity = createAsyncThunk('tools/listItemActivity', async (query: ListItemActivityQuery) => {
  const argumentsKeys = [
    'activityBegin',
    'activityEnd',
    'activityTypeIds',
    'itemId',
    'vehicleId',
    'locationVisitId',
    'siteId',
    'sortBy',
    'offset',
    'limit',
    'options',
  ];

  return itemsClient.listItemActivity.apply(
    this,
    argumentsKeys.map((key) => query[key]) as [
      activityBegin?: string,
      activityEnd?: string,
      activityTypeIds?: string,
      itemId?: string,
      vehicleId?: string,
      locationVisitId?: string,
      siteId?: string,
      sortBy?: string,
      offset?: number,
      limit?: number,
      options?: Record<string, unknown>,
    ],
  );
});

export const listItemsLeftBehindAgg = createAsyncThunk('tools/listItemsLeftBehindAgg', async (query: ListItemsLeftBehindAggQuery) => {
  const argumentsKeys = ['sortBy', 'offset', 'limit', 'options'];
  return itemsClient.listItemsLeftBehindAgg.apply(
    this,
    argumentsKeys.map((key) => query[key]) as [sortBy?: string, offset?: number, limit?: number, options?: Record<string, unknown>],
  );
});
export const listItemsRecoveredAgg = createAsyncThunk('tools/listItemsRecoveredAgg', async (query: ListItemsLeftBehindAggQuery) => {
  const argumentsKeys = ['sortBy', 'offset', 'limit', 'options'];
  return itemsClient.listItemsRecoveredAgg.apply(
    this,
    argumentsKeys.map((key) => query[key]) as [sortBy?: string, offset?: number, limit?: number, options?: Record<string, unknown>],
  );
});
export const listItemsRecoveryProgress = createAsyncThunk('tools/listItemsRecoveryProgress', async (query: ListItemsRecoveryProgressQuery) => {
  const argumentsKeys = ['todayStart', 'sortBy', 'offset', 'limit', 'todayEnd', 'options'];
  return itemsClient.listItemsRecoveryProgress.apply(
    this,
    argumentsKeys.map((key) => query[key]) as [
      todayStart: string,
      sortBy?: string,
      offset?: number,
      limit?: number,
      todayEnd?: string,
      options?: Record<string, unknown>,
    ],
  );
});

export const toolsSlice = createSlice({
  name: 'tools',
  initialState: {
    productTools: listProductToolsAdapter.getInitialState(),
    toolDetails: toolDetailsAdapter.getInitialState(),
    toolDependencies: toolDependenciesAdapter.getInitialState(),
    itemsLeftBehindAgg: listItemsLeftBehindAggAdapter.getInitialState(),
    listItemsRecoveredAgg: listItemsRecoveredAggAdapter.getInitialState(),
    itemsRecoveryProgress: listItemsRecoveryProgressAdapter.getInitialState(),
    listItemActivity: listItemActivityAdapter.getInitialState(),
    decodes: [],
    equipmentSummary: [],
    utilizationHistory: [],
    equipmentBreakdown: [],
    productUtilization: [],
    productUtilizationAll: [],
    jobHistory: {},
    deploymentStats: {},
    itemTimeline: {},
    itemRequestNew: createToolAdapter.getInitialState(),
  } as ProductToolsState,
  reducers: {
    removeTool: (state, action: PayloadAction<string>) => {
      toolDetailsAdapter.removeOne(state.toolDetails, action.payload);
      listProductToolsAdapter.removeOne(state.productTools, action.payload);
    },
    removeItemNextSerialNumber: (state) => {
      state.itemNextSerialNumber = undefined;
    },
    setLastFormData: (state, action: PayloadAction<LastFormDataType | undefined>) => {
      state.lastFormData = action.payload;
    },
    clearAddAnother: (state) => {
      state.itemRequestNew = createToolAdapter.getInitialState();
    },
  },
  extraReducers: (builder) => {
    builder.addCase(listActivityDecodes.pending, (state: ProductToolsState) => {
      state.error = undefined;
      state.fetching = { ...(state.fetching || {}), listActivityDecodes: true };
    });
    builder.addCase(listActivityDecodes.fulfilled, (state: ProductToolsState, action: AnyAction) => {
      state.fetching = { ...state.fetching, listActivityDecodes: false };
      state.decodes = action.payload.data.result;
    });
    builder.addCase(listActivityDecodes.rejected, (state: ProductToolsState, action: AnyAction) => {
      state.error = action.error;
      state.fetching = { ...state.fetching, listActivityDecodes: false };
    });
    builder.addCase(listProductTools.pending, (state: ProductToolsState) => {
      state.error = undefined;
      state.fetching = { ...(state.fetching || {}), listProductTools: true };
    });
    builder.addCase(listProductTools.fulfilled, (state: ProductToolsState, action: AnyAction) => {
      state.fetching = { ...state.fetching, listProductTools: false };
      if (action.payload.data.result) {
        listProductToolsAdapter.upsertOne(state.productTools, { id: keysToId(action.meta.arg), ...action.payload.data });
      }
    });
    builder.addCase(listProductTools.rejected, (state: ProductToolsState, action: AnyAction) => {
      state.error = action.error;
      state.fetching = { ...state.fetching, listProductTools: false };
    });

    builder.addCase(getToolById.pending, (state: ProductToolsState) => {
      state.error = undefined;
      state.fetching = { ...(state.fetching || {}), getToolById: true };
    });
    builder.addCase(getToolById.fulfilled, (state: ProductToolsState, action: AnyAction) => {
      state.fetching = { ...state.fetching, getToolById: false };
      toolDetailsAdapter.upsertOne(state.toolDetails, { id: action.payload.data.result.item_id, ...action.payload.data.result });
    });
    builder.addCase(getToolById.rejected, (state: ProductToolsState, action: AnyAction) => {
      state.error = action.error;
      state.fetching = { ...state.fetching, getToolById: false };
    });

    builder.addCase(updateTool.pending, (state: ProductToolsState) => {
      state.error = undefined;
      state.updating = true;
    });
    builder.addCase(updateTool.fulfilled, (state: ProductToolsState, action: AnyAction) => {
      state.updating = false;
      if (action.payload.data?.result) {
        // TODO check how and update list. BUG? different responses from Details and Update
        // console.log('action.meta.arg', action.payload.data.result);
        // toolDetailsAdapter.updateOne(state.toolDetails, { id: action.payload.data.result.id, changes: action.payload.data.result });
      }
    });
    builder.addCase(updateTool.rejected, (state: ProductToolsState, action: AnyAction) => {
      state.error = action.error;
      state.updating = false;
    });

    builder.addCase(toolCategories.pending, (state: ProductToolsState) => {
      state.error = undefined;
      state.fetching = { ...(state.fetching || {}), toolCategories: true };
    });
    builder.addCase(toolCategories.fulfilled, (state: ProductToolsState, action: AnyAction) => {
      state.fetching = { ...state.fetching, toolCategories: false };
      state.toolCategories = action.payload.data.result;
    });
    builder.addCase(toolCategories.rejected, (state: ProductToolsState, action: AnyAction) => {
      state.error = action.error;
      state.fetching = { ...state.fetching, toolCategories: false };
    });

    builder.addCase(itemEquipmentSummary.pending, (state: ProductToolsState) => {
      state.error = undefined;
      state.fetching = { ...(state.fetching || {}), itemEquipmentSummary: true };
    });
    builder.addCase(itemEquipmentSummary.fulfilled, (state: ProductToolsState, action: AnyAction) => {
      state.fetching = { ...state.fetching, itemEquipmentSummary: false };
      state.equipmentSummary = action.payload.data.result;
    });
    builder.addCase(itemEquipmentSummary.rejected, (state: ProductToolsState, action: AnyAction) => {
      state.error = action.error;
      state.fetching = { ...state.fetching, itemEquipmentSummary: false };
    });

    builder.addCase(itemUtilizationHistory.pending, (state: ProductToolsState) => {
      state.error = undefined;
      state.fetching = { ...(state.fetching || {}), itemUtilizationHistory: true };
    });
    builder.addCase(itemUtilizationHistory.fulfilled, (state: ProductToolsState, action: AnyAction) => {
      state.fetching = { ...state.fetching, itemUtilizationHistory: false };
      state.utilizationHistory = action.payload.data.result;
    });
    builder.addCase(itemUtilizationHistory.rejected, (state: ProductToolsState, action: AnyAction) => {
      state.error = action.error;
      state.fetching = { ...state.fetching, itemUtilizationHistory: false };
    });

    builder.addCase(itemEquipmentBreakdown.pending, (state: ProductToolsState) => {
      state.error = undefined;
      state.fetching = { ...(state.fetching || {}), itemEquipmentBreakdown: true };
    });
    builder.addCase(itemEquipmentBreakdown.fulfilled, (state: ProductToolsState, action: AnyAction) => {
      state.fetching = { ...state.fetching, itemEquipmentBreakdown: false };
      state.equipmentBreakdown = action.payload.data.result;
    });
    builder.addCase(itemEquipmentBreakdown.rejected, (state: ProductToolsState, action: AnyAction) => {
      state.error = action.error;
      state.fetching = { ...state.fetching, itemEquipmentBreakdown: false };
    });

    builder.addCase(itemProductUtilization.pending, (state: ProductToolsState) => {
      state.error = undefined;
      state.fetching = { ...(state.fetching || {}), itemProductUtilization: true };
    });
    builder.addCase(itemProductUtilization.fulfilled, (state: ProductToolsState, action: AnyAction) => {
      state.fetching = { ...state.fetching, itemProductUtilization: false };
      state.productUtilization = action.payload.data.result;
    });
    builder.addCase(itemProductUtilization.rejected, (state: ProductToolsState, action: AnyAction) => {
      state.error = action.error;
      state.fetching = { ...state.fetching, itemProductUtilization: false };
    });
    builder.addCase(itemProductUtilizationAll.pending, (state: ProductToolsState) => {
      state.error = undefined;
      state.fetching = { ...(state.fetching || {}), itemProductUtilizationAll: true };
    });
    builder.addCase(itemProductUtilizationAll.fulfilled, (state: ProductToolsState, action: AnyAction) => {
      state.fetching = { ...state.fetching, itemProductUtilizationAll: false };
      state.productUtilizationAll = action.payload.data.result;
    });
    builder.addCase(itemProductUtilizationAll.rejected, (state: ProductToolsState, action: AnyAction) => {
      state.error = action.error;
      state.fetching = { ...state.fetching, itemProductUtilizationAll: false };
    });

    builder.addCase(itemJobHistory.pending, (state: ProductToolsState) => {
      state.error = undefined;
      state.fetching = { ...(state.fetching || {}), itemJobHistory: true };
    });
    builder.addCase(itemJobHistory.fulfilled, (state: ProductToolsState, action: AnyAction) => {
      state.fetching = { ...state.fetching, itemJobHistory: false };
      // Keyed by scope so a product page's fetch cannot overwrite the
      // dashboard's organization-wide series, and vice versa.
      state.jobHistory = { ...(state.jobHistory || {}), [action.meta.arg || 'organization']: action.payload.data.result };
    });
    builder.addCase(itemJobHistory.rejected, (state: ProductToolsState, action: AnyAction) => {
      state.error = action.error;
      state.fetching = { ...state.fetching, itemJobHistory: false };
    });

    builder.addCase(itemDeploymentStats.pending, (state: ProductToolsState) => {
      state.error = undefined;
      state.fetching = { ...(state.fetching || {}), itemDeploymentStats: true };
    });
    builder.addCase(itemDeploymentStats.fulfilled, (state: ProductToolsState, action: AnyAction) => {
      state.fetching = { ...state.fetching, itemDeploymentStats: false };
      state.deploymentStats = {
        ...(state.deploymentStats || {}),
        [action.meta.arg.itemId || action.meta.arg.productId || '']: action.payload.data.result,
      };
    });
    builder.addCase(itemDeploymentStats.rejected, (state: ProductToolsState, action: AnyAction) => {
      state.error = action.error;
      state.fetching = { ...state.fetching, itemDeploymentStats: false };
    });

    builder.addCase(itemTimeline.pending, (state: ProductToolsState) => {
      state.error = undefined;
      state.fetching = { ...(state.fetching || {}), itemTimeline: true };
    });
    builder.addCase(itemTimeline.fulfilled, (state: ProductToolsState, action: AnyAction) => {
      state.fetching = { ...state.fetching, itemTimeline: false };
      state.itemTimeline = { ...(state.itemTimeline || {}), [action.meta.arg]: action.payload.data.result };
    });
    builder.addCase(itemTimeline.rejected, (state: ProductToolsState, action: AnyAction) => {
      state.error = action.error;
      state.fetching = { ...state.fetching, itemTimeline: false };
    });

    builder.addCase(getToolDependencies.pending, (state: ProductToolsState) => {
      state.error = undefined;
      state.fetching = { ...(state.fetching || {}), getToolDependencies: true };
    });
    builder.addCase(getToolDependencies.fulfilled, (state: ProductToolsState, action: AnyAction) => {
      state.fetching = { ...state.fetching, getToolDependencies: false };
      toolDependenciesAdapter.upsertOne(state.toolDependencies, action.payload.data.result);
    });
    builder.addCase(getToolDependencies.rejected, (state: ProductToolsState, action: AnyAction) => {
      state.error = action.error;
      state.fetching = { ...state.fetching, getToolDependencies: false };
    });

    builder.addCase(getItemNextSerialNumber.pending, (state: ProductToolsState) => {
      state.error = undefined;
      state.fetching = { ...(state.fetching || {}), getItemNextSerialNumber: true };
    });
    builder.addCase(getItemNextSerialNumber.fulfilled, (state: ProductToolsState, action: AnyAction) => {
      state.fetching = { ...state.fetching, getItemNextSerialNumber: false };
      state.itemNextSerialNumber = action.payload.data.result.toString();
    });
    builder.addCase(getItemNextSerialNumber.rejected, (state: ProductToolsState, action: AnyAction) => {
      state.error = action.error;
      state.fetching = { ...state.fetching, getItemNextSerialNumber: false };
    });
    builder.addCase(listItemsLeftBehindAgg.pending, (state: ProductToolsState) => {
      state.error = undefined;
      state.fetching = { ...(state.fetching || {}), listItemsLeftBehindAgg: true };
    });
    builder.addCase(listItemsLeftBehindAgg.fulfilled, (state: ProductToolsState, action: AnyAction) => {
      state.fetching = { ...state.fetching, listItemsLeftBehindAgg: false };
      if (action.payload.data.result) {
        listItemsLeftBehindAggAdapter.upsertOne(state.itemsLeftBehindAgg, { id: keysToId(action.meta.arg), ...action.payload.data });
      }
    });
    builder.addCase(listItemsLeftBehindAgg.rejected, (state: ProductToolsState, action: AnyAction) => {
      state.error = action.error;
      state.fetching = { ...state.fetching, listItemsLeftBehindAgg: false };
    });
    builder.addCase(listItemsRecoveredAgg.pending, (state: ProductToolsState) => {
      state.error = undefined;
      state.fetching = { ...(state.fetching || {}), listItemsRecoveredAgg: true };
    });
    builder.addCase(listItemsRecoveredAgg.fulfilled, (state: ProductToolsState, action: AnyAction) => {
      state.fetching = { ...state.fetching, listItemsRecoveredAgg: false };
      if (action.payload.data.result) {
        listItemsRecoveredAggAdapter.upsertOne(state.listItemsRecoveredAgg, { id: keysToId(action.meta.arg), ...action.payload.data });
      }
    });
    builder.addCase(listItemsRecoveredAgg.rejected, (state: ProductToolsState, action: AnyAction) => {
      state.error = action.error;
      state.fetching = { ...state.fetching, listItemsRecoveredAgg: false };
    });
    builder.addCase(listItemsRecoveryProgress.pending, (state: ProductToolsState) => {
      state.error = undefined;
      state.fetching = { ...(state.fetching || {}), listItemsRecoveryProgress: true };
    });
    builder.addCase(listItemsRecoveryProgress.fulfilled, (state: ProductToolsState, action: AnyAction) => {
      state.fetching = { ...state.fetching, listItemsRecoveryProgress: false };
      if (action.payload.data.result) {
        listItemsRecoveryProgressAdapter.upsertOne(state.itemsRecoveryProgress, { id: keysToId(action.meta.arg), ...action.payload.data });
      }
    });
    builder.addCase(listItemsRecoveryProgress.rejected, (state: ProductToolsState, action: AnyAction) => {
      state.error = action.error;
      state.fetching = { ...state.fetching, listItemsRecoveryProgress: false };
    });
    builder.addCase(listItemActivity.pending, (state: ProductToolsState) => {
      state.error = undefined;
      state.fetching = { ...(state.fetching || {}), listItemActivity: true };
    });
    builder.addCase(listItemActivity.fulfilled, (state: ProductToolsState, action: AnyAction) => {
      state.fetching = { ...state.fetching, listItemActivity: false };
      if (action.payload.data?.result) {
        listItemActivityAdapter.upsertOne(state.listItemActivity, { id: keysToId(action.meta.arg), ...action.payload.data });
      }
    });
    builder.addCase(listItemActivity.rejected, (state: ProductToolsState, action: AnyAction) => {
      state.error = action.error;
      state.fetching = { ...state.fetching, listItemActivity: false };
    });
    builder.addCase(createTool.fulfilled, (state: ProductToolsState, action: AnyAction) => {
      if (action.payload.data?.result) {
        createToolAdapter.upsertOne(state.itemRequestNew, {
          id: action.payload.data.result.id,
          created_at: DateTime.local().toUTC().toISO(),
          ...action.meta.arg,
        });
      }
    });
  },
});

export const { selectById: selectProductToolsById } = listProductToolsAdapter.getSelectors((state: RootState) => state.tools.productTools);
export const { selectById: selectToolDetailsById } = toolDetailsAdapter.getSelectors((state: RootState) => state.tools.toolDetails);
export const { selectById: selectToolDependenciesById } = toolDependenciesAdapter.getSelectors((state: RootState) => state.tools.toolDependencies);
export const { selectById: selectItemsLeftBehindAggById } = listItemsLeftBehindAggAdapter.getSelectors((state: RootState) => state.tools.itemsLeftBehindAgg);
export const { selectById: selectItemsRecoveredAggById } = listItemsRecoveredAggAdapter.getSelectors((state: RootState) => state.tools.listItemsRecoveredAgg);
export const { selectById: selectItemsRecoveryProgressById } = listItemsRecoveryProgressAdapter.getSelectors(
  (state: RootState) => state.tools.itemsRecoveryProgress,
);
export const { selectById: listItemActivityById } = listItemActivityAdapter.getSelectors((state: RootState) => state.tools.listItemActivity);
export const { selectAll: listItemRequestNewAll } = createToolAdapter.getSelectors((state: RootState) => state.tools.itemRequestNew);

export const listItemRequestNewAllMapped = createSelector([listItemRequestNewAll], (items): ItemRequestNewItemMappedType[] =>
  items.map((item) => ({
    ...item,
    tags_mapped: item.tags?.join(', ') ?? '',
    created_at_mapped: DateTime.fromISO(item.created_at).toLocaleString(DateTime.DATE_MED),
  })),
);

const selectListItemActivity = createSelector(
  [getUserAbilities, listItemActivityById],
  (abilities, items): ListItemActivityListType => ({
    pagination: getPagination(items),
    result: items?.result?.map((item) => ({
      ...item,
      periodMapped: getPeriodMapped(item.first_visit, item.last_visit),
      productIdentifierMapped: abilities.can('Container') ? item.identifier ?? '' : `${item.product_name}${item.identifier ? ` — ${item.identifier}` : ''}`,
    })),
  }),
);
export const getListItemActivity =
  (keys: ListItemActivityQuery) =>
  (state: RootState): ListItemActivityListType =>
    selectListItemActivity(state, keysToId(keys));

const selectProductToolsByIdMapped = createSelector(
  [selectProductToolsById],
  (items): ToolsType => ({
    pagination: getPagination(items),
    result: items?.result.map((item) => ({
      ...item,
      activeMapped: item.active ? 'Active' : 'Disabled',
      identifierProductMapped: `${item.identifier}${item.identifier && item.product_name && ' - '}${item.product_name}`,
      lastSeenLocationMapped: getLastSeenLocation(item),
      // tracked_mapped: item.vehicle.location_type !== 'At Home' ? item.vehicle.item_cnt_total || 0 : 0,
      // use_mapped: item.vehicle.location_type !== 'At Home' ? item.vehicle.item_cnt_in_use || 0 : 0,
      // leftBehind_mapped: item.vehicle.location_type !== 'At Home' ? item.vehicle.item_cnt_left_behind || 0 : 0,
      ...(() => {
        if (!item.tracking_site_name && !item.payload_type_name) {
          return { lastSeenMapped: 'On Road', lastSeenType: 'payloads' };
        }
        return item.tracking_site_name
          ? { lastSeenMapped: item.tracking_site_name, lastSeenType: 'locationVisit' }
          : { lastSeenMapped: item.payload_type_name, lastSeenType: 'payloads' };
      })(),
    })),
  }),
);

const selectItemsLeftBehindAggByIdSelector = createSelector(
  [selectItemsLeftBehindAggById],
  (items): ItemsLeftBehindAgg => ({
    pagination: getPagination(items),
    totals: items?.totals,
    result: items?.result,
  }),
);

export const getItemsLeftBehindAggById =
  (keys: ProductItemsQuery) =>
  (state: RootState): ItemsLeftBehindAgg =>
    selectItemsLeftBehindAggByIdSelector(state, keysToId(keys));

const selectItemsRecoveredAggByIdSelector = createSelector(
  [selectItemsRecoveredAggById],
  (items): ItemsRecoveredAgg => ({
    pagination: getPagination(items),
    totals: items?.totals,
    result: items?.result,
  }),
);

export const getItemsRecoveredAggById =
  (keys: ProductItemsQuery) =>
  (state: RootState): ItemsRecoveredAgg =>
    selectItemsRecoveredAggByIdSelector(state, keysToId(keys));

const selectItemsRecoveryProgressByIdSelector = createSelector(
  [selectItemsRecoveryProgressById],
  (items): ItemsRecoveryProgress => ({
    pagination: getPagination(items),
    totals: items?.totals,
    result: items?.result,
  }),
);

export const getItemsRecoveryProgressById =
  (keys: ListItemsRecoveryProgressQuery) =>
  (state: RootState): ItemsRecoveryProgress =>
    selectItemsRecoveryProgressByIdSelector(state, keysToId(keys));

export const getItemsRecoveryProgressFetching = (state: RootState): boolean => state.tools.fetching?.listItemsRecoveryProgress !== false;

export const getToolDetailsById =
  (itemId = '') =>
  (state: RootState): QueryItemDetails | undefined =>
    selectToolDetailsById(state, itemId);

const getToolCategoriesGroupsSelector = createSelector([(state: RootState) => state.tools.toolCategories], (categories?: QueryItemCategory[]) =>
  categories ? groupBy(categories, 'group_name') : undefined,
);

export const getToolDependenciesById =
  (itemId = '') =>
  (state: RootState): QueryDependencies | undefined =>
    selectToolDependenciesById(state, itemId);

// The "Status" rows of the item-categories endpoint, in the order the backend
// sorts them, shaped for the KPI tile. They are current-state counts, so no
// history is involved.
export const getToolStatusKpis = createSelector([(state: RootState) => state.tools.toolCategories], (categories) =>
  (categories || [])
    .filter((category) => category.group_name === 'Status' && category.filter_display !== 'All')
    .map((category) => ({
      label: category.filter_display || '',
      amount: Number(category.item_count || 0),
      scanStatus: category.filter_id || '',
    })),
);

export const getToolCategoriesGroups = (state: RootState): { [key: string]: QueryItemCategory[] } | undefined => getToolCategoriesGroupsSelector(state);

export const getProductToolsById =
  (keys: ProductItemsQuery) =>
  (state: RootState): ToolsType =>
    selectProductToolsByIdMapped(state, keysToId(keys));

export const productToolsFetching = (state: RootState): boolean => state.tools.fetching?.listProductTools !== false;
export const productFetching = (state: RootState): boolean => state.tools.fetching?.getToolById !== false;
export const toolCategoriesFetching = (state: RootState): boolean => state.tools.fetching?.toolCategories !== false;
export const itemEquipmentSummaryFetching = (state: RootState): boolean => state.tools.fetching?.itemEquipmentSummary !== false;
export const itemUtilizationHistoryFetching = (state: RootState): boolean => state.tools.fetching?.itemUtilizationHistory !== false;
export const itemEquipmentBreakdownFetching = (state: RootState): boolean => state.tools.fetching?.itemEquipmentBreakdown !== false;
export const itemProductUtilizationFetching = (state: RootState): boolean => state.tools.fetching?.itemProductUtilization !== false;
export const itemProductUtilizationAllFetching = (state: RootState): boolean => state.tools.fetching?.itemProductUtilizationAll !== false;
export const itemJobHistoryFetching = (state: RootState): boolean => state.tools.fetching?.itemJobHistory !== false;
export const itemDeploymentStatsFetching = (state: RootState): boolean => state.tools.fetching?.itemDeploymentStats !== false;
export const getDeploymentStats = (scopeId: string) => (state: RootState) => state.tools.deploymentStats?.[scopeId];
export const getItemTimeline = (itemId: string) => (state: RootState) => state.tools.itemTimeline?.[itemId];
export const itemTimelineFetching = (state: RootState): boolean => state.tools.fetching?.itemTimeline !== false;

// A year of daily utilization. The backend emits one row per manufacturer per
// category per day; this rolls those cells up into the groupings the dashboard
// asks for. Percentages are always recomputed from the summed counts, because
// averaging per-cell percentages would weight a 26-item cell like a 1,610-item
// one.
export const TOTAL_SERIES = 'Total';

const rollUp = (cells: UtilizationPointType[][]): UtilizationPointType[] => {
  const byDate: { [date: string]: UtilizationPointType } = {};
  cells.forEach((points) =>
    points.forEach((point) => {
      const running = byDate[point.date];
      byDate[point.date] = running
        ? {
            ...running,
            onsite: running.onsite + point.onsite,
            inTransit: running.inTransit + point.inTransit,
            warehouse: running.warehouse + point.warehouse,
            totalEquipment: running.totalEquipment + point.totalEquipment,
          }
        : { ...point };
    }),
  );
  return Object.keys(byDate)
    .sort()
    .map((date) => {
      const point = byDate[date];
      return { ...point, trackedPct: point.totalEquipment ? (point.onsite / point.totalEquipment) * 100 : 0 };
    });
};

export const getUtilizationSeries = createSelector([(state: RootState) => state.tools.utilizationHistory], (rows) => {
  // cells[manufacturerId][category] is the raw series the backend returned.
  const cells: { [manufacturerId: string]: { [category: string]: UtilizationPointType[] } } = {};

  (rows || []).forEach((row) => {
    if (!row.date || !row.category || !row.manufacturer_id) {
      return;
    }
    const point = {
      date: row.date,
      onsite: row.onsite_count || 0,
      inTransit: row.in_transit_count || 0,
      warehouse: row.warehouse_count || 0,
      totalEquipment: row.total_equipment || 0,
      trackedPct: row.tracked_pct || 0,
    };
    const forManufacturer = cells[row.manufacturer_id] || {};
    forManufacturer[row.category] = [...(forManufacturer[row.category] || []), point];
    cells[row.manufacturer_id] = forManufacturer;
  });

  const manufacturerIds = Object.keys(cells);
  const categoryNames = Array.from(new Set(manufacturerIds.flatMap((id) => Object.keys(cells[id]))));

  // byCategory[category] spans every manufacturer; Total spans everything.
  const byCategory: { [category: string]: UtilizationPointType[] } = {};
  categoryNames.forEach((category) => {
    byCategory[category] = rollUp(manufacturerIds.map((id) => cells[id][category] || []));
  });
  byCategory[TOTAL_SERIES] = rollUp(manufacturerIds.flatMap((id) => Object.keys(cells[id]).map((category) => cells[id][category])));

  // byManufacturer[id][category] narrows to one manufacturer, with its own
  // Total across the categories it sells.
  const byManufacturer: { [manufacturerId: string]: { [category: string]: UtilizationPointType[] } } = {};
  manufacturerIds.forEach((id) => {
    const forManufacturer: { [category: string]: UtilizationPointType[] } = {};
    Object.keys(cells[id]).forEach((category) => {
      forManufacturer[category] = cells[id][category];
    });
    forManufacturer[TOTAL_SERIES] = rollUp(Object.keys(cells[id]).map((category) => cells[id][category]));
    byManufacturer[id] = forManufacturer;
  });

  return { byCategory, byManufacturer };
});

// The product-grained breakdown, rolled up for each screen that reads it. Every
// row is one product, and a product has exactly one manufacturer and one
// category, so no roll-up double-counts.
const breakdownRows = (state: RootState) => state.tools.equipmentBreakdown || [];

const withRates = <T extends { items: number; tracked: number }>(row: T) => ({
  ...row,
  notTracked: row.items - row.tracked,
  trackedPct: row.items ? (row.tracked / row.items) * 100 : 0,
});

// Dashboard: one row per manufacturer, narrowed to a category or across all.
export const getManufacturerSummary = createSelector([breakdownRows], (rows) => (category: string) => {
  const byManufacturer: {
    [id: string]: { manufacturerId: string; manufacturerName: string; imageKey: string; products: number; items: number; tracked: number };
  } = {};

  rows
    .filter((row) => category === TOTAL_SERIES || row.category === category)
    .forEach((row) => {
      const id = row.manufacturer_id || '';
      const running = byManufacturer[id];
      byManufacturer[id] = {
        manufacturerId: id,
        manufacturerName: row.manufacturer_name || '',
        imageKey: row.manufacturer_image || '',
        products: (running?.products || 0) + 1,
        items: (running?.items || 0) + (row.item_count || 0),
        tracked: (running?.tracked || 0) + (row.tracked_count || 0),
      };
    });

  return Object.keys(byManufacturer)
    .map((id) => withRates(byManufacturer[id]))
    .sort((a, b) => b.items - a.items);
});

// The six scan-status tiles the tools dashboard shows, from the same rows every
// other equipment figure comes from, so the dashboard, a manufacturer and a
// product all count the same population the same way. Pass a filter to narrow.
export const STATUS_KPIS: { label: string; scanStatus: string; field: keyof QueryItemEquipmentBreakdownRow }[] = [
  { label: 'On Board', scanStatus: '1', field: 'on_board_count' },
  { label: 'In Use', scanStatus: '2', field: 'in_use_count' },
  { label: 'Left Onsite', scanStatus: '3', field: 'left_onsite_count' },
  { label: 'Not Tracked', scanStatus: '0', field: 'not_tracked_count' },
  { label: 'Never Seen', scanStatus: '-1', field: 'never_seen_count' },
  { label: 'Missing', scanStatus: '5', field: 'missing_count' },
];

export const getStatusKpis = createSelector([breakdownRows], (rows) => (filter: { manufacturerId?: string; productId?: string; category?: string } = {}) => {
  const wanted = rows.filter(
    (row) =>
      (!filter.manufacturerId || row.manufacturer_id === filter.manufacturerId) &&
      (!filter.productId || row.product_id === filter.productId) &&
      (!filter.category || filter.category === TOTAL_SERIES || row.category === filter.category),
  );
  return STATUS_KPIS.map((kpi) => ({
    label: kpi.label,
    scanStatus: kpi.scanStatus,
    amount: wanted.reduce((sum, row) => sum + ((row[kpi.field] as number) || 0), 0),
  }));
});

// One product's current counts, for the product details page.
export const getEquipmentBreakdownForProduct = createSelector([breakdownRows], (rows) => (productId: string) => {
  const row = rows.find((candidate) => candidate.product_id === productId);
  return row
    ? withRates({
        productName: row.product_name || '',
        category: row.category || 'Other',
        items: row.item_count || 0,
        tracked: row.tracked_count || 0,
      })
    : undefined;
});

// Manufacturer page, top section: the categories this manufacturer actually
// sells, never the empty ones.
export const getManufacturerCategories = createSelector([breakdownRows], (rows) => (manufacturerId: string) => {
  const byCategory: { [category: string]: { category: string; products: number; items: number; tracked: number } } = {};

  rows
    .filter((row) => row.manufacturer_id === manufacturerId)
    .forEach((row) => {
      const category = row.category || 'Other';
      const running = byCategory[category];
      byCategory[category] = {
        category,
        products: (running?.products || 0) + 1,
        items: (running?.items || 0) + (row.item_count || 0),
        tracked: (running?.tracked || 0) + (row.tracked_count || 0),
      };
    });

  return Object.keys(byCategory)
    .map((category) => withRates(byCategory[category]))
    .sort((a, b) => b.items - a.items);
});

// Manufacturer page, bottom section: its individual products, optionally
// narrowed to the category selected above.
export const getManufacturerProducts = createSelector(
  [breakdownRows],
  (rows) => (manufacturerId: string, category: string) =>
    rows
      .filter((row) => row.manufacturer_id === manufacturerId && (category === TOTAL_SERIES || row.category === category))
      .map((row) =>
        withRates({
          productId: row.product_id || '',
          productName: row.product_name || '',
          sku: row.product_sku || '',
          category: row.category || 'Other',
          items: row.item_count || 0,
          tracked: row.tracked_count || 0,
        }),
      )
      .sort((a, b) => b.items - a.items),
);

// Dashboard, Products view: one row per product, narrowed to a category or
// across all. Each row carries its manufacturer so the table can show the logo
// and link to the manufacturer without a second lookup.
export const getProductSummary = createSelector(
  [breakdownRows],
  (rows) => (category: string) =>
    rows
      .filter((row) => category === TOTAL_SERIES || row.category === category)
      .map((row) =>
        withRates({
          productId: row.product_id || '',
          productName: row.product_name || '',
          sku: row.product_sku || '',
          category: row.category || 'Other',
          manufacturerId: row.manufacturer_id || '',
          manufacturerName: row.manufacturer_name || '',
          imageKey: row.manufacturer_image || '',
          items: row.item_count || 0,
          tracked: row.tracked_count || 0,
        }),
      )
      .sort((a, b) => b.items - a.items || a.productName.localeCompare(b.productName)),
);

// One series per product, keyed by product id.
const seriesByProduct = (rows?: QueryItemUtilizationPoint[]): { [productId: string]: UtilizationPointType[] } => {
  const byProduct: { [productId: string]: UtilizationPointType[] } = {};
  (rows || []).forEach((row) => {
    if (!row.date || !row.product_id) {
      return;
    }
    byProduct[row.product_id] = [
      ...(byProduct[row.product_id] || []),
      {
        date: row.date,
        onsite: row.onsite_count || 0,
        inTransit: row.in_transit_count || 0,
        warehouse: row.warehouse_count || 0,
        totalEquipment: row.total_equipment || 0,
        trackedPct: row.tracked_pct || 0,
      },
    ];
  });
  return byProduct;
};

// From the manufacturer-scoped fetch; the manufacturer page hides products
// outside the selected category using the category each product carries.
export const getProductUtilization = createSelector([(state: RootState) => state.tools.productUtilization], seriesByProduct);

// From the organization-wide fetch, for the tools dashboard's Products view.
export const getProductUtilizationAll = createSelector([(state: RootState) => state.tools.productUtilizationAll], seriesByProduct);

// Daily count of distinct job sites holding each cohort's equipment, keyed the
// same way as the utilization series (category names plus 'Total'). The backend
// counts every level separately because the series overlap: a site holding two
// categories counts in both, and in Total once.
export const getJobHistory = createSelector([(state: RootState) => state.tools.jobHistory], (scopes) => (scope = 'organization') => {
  const bySeries: { [series: string]: JobPointType[] } = {};
  (scopes?.[scope] || []).forEach((row) => {
    if (!row.date || !row.series) {
      return;
    }
    bySeries[row.series] = [...(bySeries[row.series] || []), { date: row.date, jobCount: row.job_count || 0 }];
  });
  return bySeries;
});

// The busiest day and the mean of a job-count series.
export const jobStats = (points?: JobPointType[]): { max: number; avg: number } | undefined => {
  if (!points || !points.length) {
    return undefined;
  }
  return {
    max: points.reduce((highest, point) => Math.max(highest, point.jobCount), 0),
    avg: points.reduce((sum, point) => sum + point.jobCount, 0) / points.length,
  };
};

// The high-water mark and mean of a series' daily tracked share.
export const utilizationStats = (points?: UtilizationPointType[]): { max: number; avg: number } | undefined => {
  if (!points || !points.length) {
    return undefined;
  }
  return {
    max: points.reduce((highest, point) => Math.max(highest, point.trackedPct), 0),
    avg: points.reduce((sum, point) => sum + point.trackedPct, 0) / points.length,
  };
};

// The org-wide equipment breakdown by product category, plus the share of each
// category that is tracked (at a job site). Percentages are of the category's
// own total, so a category with no items reads 0% rather than NaN.
export const getEquipmentSummary = createSelector([(state: RootState) => state.tools.equipmentSummary], (rows) => {
  const categories = (rows || []).map((row) => {
    const total = row.total_count || 0;
    const tracked = row.tracked_count || 0;
    const notTracked = row.not_tracked_count || 0;
    const pickup = row.pickup_count || 0;
    return {
      category: row.category || 'Other',
      total,
      tracked,
      notTracked,
      pickup,
      // On a job and not yet flagged for pickup: tracked minus pickup, so the
      // home dashboard's three groups add up to the total. The tools dashboard
      // keeps reading `tracked`.
      onJob: tracked - pickup,
      trackedPct: total ? (tracked / total) * 100 : 0,
      notTrackedPct: total ? (notTracked / total) * 100 : 0,
      pickupPct: total ? (pickup / total) * 100 : 0,
      onJobPct: total ? ((tracked - pickup) / total) * 100 : 0,
    };
  });
  const total = categories.reduce((sum, row) => sum + row.total, 0);
  const tracked = categories.reduce((sum, row) => sum + row.tracked, 0);
  const notTracked = categories.reduce((sum, row) => sum + row.notTracked, 0);
  const pickup = categories.reduce((sum, row) => sum + row.pickup, 0);
  return {
    categories,
    total,
    tracked,
    notTracked,
    pickup,
    onJob: tracked - pickup,
    trackedPct: total ? (tracked / total) * 100 : 0,
    notTrackedPct: total ? (notTracked / total) * 100 : 0,
    pickupPct: total ? (pickup / total) * 100 : 0,
    onJobPct: total ? ((tracked - pickup) / total) * 100 : 0,
  };
});
export const getToolDependenciesFetching = (state: RootState): boolean => state.tools.fetching?.getToolDependencies !== false;
export const getItemsLeftBehindAggByIdFetching = (state: RootState): boolean => state.tools.fetching?.listItemsLeftBehindAgg !== false;
export const getItemsRecoveredAggByIdFetching = (state: RootState): boolean => state.tools.fetching?.listItemsRecoveredAgg !== false;
export const getListItemActivityFetching = (state: RootState): boolean => state.tools.fetching?.listItemActivity !== false;
export const getActivityDecodesFetching = (state: RootState): boolean => state.tools.fetching?.listActivityDecodes !== false;

export const getActivityDecodes = (state: RootState): QueryActivityDecode[] => state.tools.decodes;
export const getItemIdentifier = (state: RootState): string | undefined => state.tools.itemNextSerialNumber;
export const getLastFormData = (state: RootState): LastFormDataType | undefined => state.tools.lastFormData;

export const { removeTool, removeItemNextSerialNumber, setLastFormData, clearAddAnother } = toolsSlice.actions;
export default toolsSlice.reducer;

const getLastSeenLocation = (item) => {
  const seen = getSeen(item.processed_at);
  const timeToShow = item.tracking_status === 'Tracked' ? DateTime.fromISO(item.processed_at).toLocaleString(DateTime.TIME_SIMPLE) : seen;

  const trackingStatus = {
    'Left Behind': `${timeToShow}||${item.tracking_site_name}`,
    Missing: `${timeToShow}||${item.last_seen_by}`,
    'In Use': `${timeToShow}||${item.tracking_site_name}`,
    Present: `${timeToShow}||${item.last_seen_by}`,
    'Not Tracked': 'Not Tracked',
    'Never Tracked': 'Never Tracked',
    'Never Seen': 'Never Seen',
    default: `${timeToShow}||${item.last_seen_by}`,
  };

  return trackingStatus[item.tracking_status] || trackingStatus.default;
};
